import { useState, useCallback, useEffect, useRef } from "react";
import { CashAndBankRepositoryImpl } from "../../data/repositories/CashAndBankRepositoryImpl";
import { GetRecentBankTransactionsUseCase } from "../../domain/use-cases/GetRecentBankTransactionsUseCase";
import type {
  GetRecentBankTransactionsParams,
  RecentBankTransactionsPeriod,
  RecentBankTransactionsStatus,
} from "../../domain/repositories/CashAndBankRepository";
import type { RecentBankTransactionItemEntity } from "../../domain/entities/RecentBankTransactionsEntity";

const repository = new CashAndBankRepositoryImpl();
const getRecentBankTransactionsUseCase = new GetRecentBankTransactionsUseCase(
  repository
);

const ITEMS_PER_PAGE = 10;

export function useRecentBankTransactions() {
  const [period, setPeriod] =
    useState<RecentBankTransactionsPeriod>("last7d");
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [filter, setFilter] =
    useState<RecentBankTransactionsStatus>("all");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<RecentBankTransactionItemEntity[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const skipNextFetchRef = useRef(false);

  const fetchActivity = useCallback(
    async (
      pageNum: number,
      overrides?: {
        period?: RecentBankTransactionsPeriod;
        filter?: RecentBankTransactionsStatus;
        date_from?: string;
        date_to?: string;
      }
    ) => {
      setLoading(true);
      setError(null);
      const p = overrides?.period ?? period;
      const f = overrides?.filter ?? filter;
      const from = overrides?.date_from ?? customStartDate;
      const to = overrides?.date_to ?? customEndDate;

      const params: GetRecentBankTransactionsParams = {
        status: f,
        page: pageNum,
        limit: ITEMS_PER_PAGE,
      };
      if (p === "custom" && from && to) {
        params.period = "custom";
        params.date_from = from;
        params.date_to = to;
      } else {
        params.period = p;
      }

      try {
        const entity =
          await getRecentBankTransactionsUseCase.execute(params);
        setRows(entity.items);
        setTotalItems(entity.pagination.total);
        setTotalPages(entity.pagination.totalPages);
        setPage(entity.pagination.page);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load transactions"
        );
        setRows([]);
        setTotalItems(0);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    [period, filter, customStartDate, customEndDate]
  );

  useEffect(() => {
    if (period === "custom") {
      return;
    }
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false;
      return;
    }
    fetchActivity(1);
  }, [period, filter, fetchActivity]);

  const handlePeriodChange = useCallback(
    (newPeriod: RecentBankTransactionsPeriod) => {
      setPeriod(newPeriod);
      setPage(1);
      if (newPeriod === "custom") {
        setShowCustomRange(true);
      } else {
        setShowCustomRange(false);
        setCustomStartDate("");
        setCustomEndDate("");
      }
    },
    []
  );

  const handleFilterChange = useCallback(
    (newFilter: RecentBankTransactionsStatus) => {
      setFilter(newFilter);
      setPage(1);
    },
    []
  );

  const handleCustomApply = useCallback(
    (start: string, end: string) => {
      setCustomStartDate(start);
      setCustomEndDate(end);
      setShowCustomRange(false);
      setPage(1);
      fetchActivity(1, {
        period: "custom",
        date_from: start,
        date_to: end,
        filter,
      });
    },
    [fetchActivity, filter]
  );

  const handleCustomRangeCancel = useCallback(() => {
    skipNextFetchRef.current = true;
    setPeriod("last7d");
    setCustomStartDate("");
    setCustomEndDate("");
    setShowCustomRange(false);
    setPage(1);
  }, []);

  const handlePageChange = useCallback(
    (newPage: number) => {
      fetchActivity(newPage);
    },
    [fetchActivity]
  );

  return {
    period,
    showCustomRange,
    customStartDate,
    customEndDate,
    filter,
    rows,
    loading,
    error,
    currentPage: page,
    totalPages,
    totalItems,
    itemsPerPage: ITEMS_PER_PAGE,
    onPeriodChange: handlePeriodChange,
    onCustomStartDateChange: setCustomStartDate,
    onCustomEndDateChange: setCustomEndDate,
    onCustomApply: handleCustomApply,
    onCustomRangeCancel: handleCustomRangeCancel,
    onFilterChange: handleFilterChange,
    onPageChange: handlePageChange,
    refetch: () => fetchActivity(page),
  };
}
